import { type ComponentPropsWithRef, type MouseEvent, type ReactElement } from 'react'

import { useCtaTracking } from '@lib/hooks/use-cta-tracking'

import { ComponentCtaButtonType } from '@constants/enums/cta'

import { updateAssetCDNUrl } from '@components/asset/utils'
import { getPictureAttributes } from '@components/contentful-content-types/asset-image/utils'
import { Button } from '@components/ui-core/button/button'
import { Picture } from '@components/ui-core/picture/picture'
import { Section } from '@components/ui-core/section/section'

import { HomepageText } from '@custom/pages/homepage/text/homepage-text'

import { type HomepageData } from '../types'

import styles from './superhuman-section.module.css'

export interface SuperhumanSectionProps extends ComponentPropsWithRef<'section'> {
  data?: HomepageData['superhumanSection']
}

export function SuperhumanSection(props: SuperhumanSectionProps): ReactElement {
  const { className, children, data, ...rest } = props
  const { heading, link, media } = data?.content || {}
  const { trackAndNavigate, isLoading } = useCtaTracking({ placement: 'Section' })

  const handleClick = (e: MouseEvent<HTMLAnchorElement> | MouseEvent<HTMLButtonElement>) => {
    if (!link?.fields?.targetUrl) {
      return
    }

    trackAndNavigate(
      ComponentCtaButtonType.Signup,
      link?.fields?.title ?? '',
      'primary',
      link?.fields?.targetUrl,
      e,
    )
  }

  const backgroundAttributes = getPictureAttributes({
    width: media?.image?.width ?? 0,
    height: media?.image?.height ?? 0,
    url: updateAssetCDNUrl(media?.image?.path),
    isSvg: false,
    cols: 12,
  })

  return (
    <Section
      style={{
        // TODO: Look into allowing this as a prop in the Section component
        // The current className gets applied to the ContentWrapper and not the section itself
        // This also is a layered background - the first layer is a linear gradient in case the video fails to load
        background: `center/cover no-repeat, linear-gradient(270deg, #554DCB 9.04%, #86CBF9 84.36%, #F7DBFF 100%)`,
      }}
      className={styles.section}
      backgroundImage={
        <Picture
          src={backgroundAttributes.src}
          srcSets={backgroundAttributes.srcSets}
          height={backgroundAttributes.height}
          width={backgroundAttributes.width}
          sizes={backgroundAttributes.sizes}
          alt={media?.image?.alt || ''}
          fit="cover"
        />
      }
      {...rest}
    >
      <HomepageText as="h2" variant="headingLarge">
        {heading}
      </HomepageText>
      <Button
        category="primary"
        text={link?.fields?.title}
        variant="heart"
        data-color-scheme="dark"
        href={link?.fields?.targetUrl}
        onClick={handleClick}
        isLoading={isLoading}
      />
    </Section>
  )
}
